import { introRequestsDashboardNotificationQuery } from ':src/domains/introRequests/queries';
import { chatsWithUnreadMessagesQuery } from ':src/domains/messages/queries';
import { upcomingTabPath } from ':src/domains/sessions';
import { OrganizationAdminPermissionName } from ':src/graphql/enums';
import usePermissions from ':src/hooks/usePermissions';
import useViewer from ':src/hooks/useViewer';
import { useQuery } from '@apollo/client';
import {
  BaseHeaderLink,
  Header as MoHeader,
  HeaderLink,
  MoNotificationBadge,
} from '@motivo/guanyin/src/components';
import { HeaderLinkType, HeaderMenuLink } from '@motivo/guanyin/src/components/Header/HeaderMenu';
import { HELP_ARTICLE_URLS } from '@motivo/guanyin/src/lookup';
import { login } from '@motivo/styx/src/auth';
import { HelpOutline } from '@mui/icons-material';
import { Avatar, Badge, Box, Divider, ListItem, Typography } from '@mui/material';
import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useMotivoBasicGateSafe } from ':src/domains/basic/useMotivoBasicGate';
import { isDemoOrganization } from ':src/domains/basic/demoOrganizations';

type HeaderProps = {
  hideLinks?: boolean;
};

export default function Header({ hideLinks }: HeaderProps) {
  const { viewer, isLoggedIn } = useViewer();
  const { fullName, mediumAvatar } = viewer;
  const { hasPermission } = usePermissions();
  const isMotivoBasicDemo = isDemoOrganization(viewer.organizationId);
  const gateContext = useMotivoBasicGateSafe();

  const showResources = isMotivoBasicDemo && gateContext?.resources;

  const { data: introRequestsDashboardNotificationData } = useQuery(
    introRequestsDashboardNotificationQuery,
    {
      fetchPolicy: 'network-only',
      skip: !isLoggedIn,
    },
  );
  const { data: chatsWithUnreadMessagesData } = useQuery(chatsWithUnreadMessagesQuery, {
    skip: !isLoggedIn,
  });

  const introRequestsDashboardNotificationTotal =
    introRequestsDashboardNotificationData?.introRequests.total;
  const unreadChatsTotal = chatsWithUnreadMessagesData?.chatsWithUnreadMessages.length;
  const totalNotificationCount =
    (introRequestsDashboardNotificationTotal || 0) + (unreadChatsTotal || 0);

  const { pathname } = useLocation();
  const isSessions = pathname.startsWith('/sessions');
  const isSupervisees = pathname.startsWith('/supervisees');
  const isSupervisors = pathname.startsWith('/supervisors');
  const isResources = pathname.startsWith('/resources');
  const isDashboard = pathname === '/';

  // ----------------------------------------
  // menu config
  // ----------------------------------------
  const headerLinks: HeaderLinkType[] = [
    { text: 'Dashboard', path: '/', isActive: isDashboard },
    { text: 'Sessions', path: upcomingTabPath, isActive: isSessions },
    ...(hasPermission(OrganizationAdminPermissionName.ProgramView)
      ? [
          {
            text: 'Supervisees',
            path: '/supervisees',
            isActive: isSupervisees,
          },
          {
            text: 'Supervisors',
            path: '/supervisors',
            isActive: isSupervisors,
          },
        ]
      : []),
    ...(showResources ? [{ text: 'Resources', path: '/resources', isActive: isResources }] : []),
  ];

  // menu has 4 sections per spec
  const menuSections: [HeaderLinkType[], HeaderLinkType[], HeaderLinkType[], HeaderLinkType[]] = [
    [],
    [],
    [],
    [],
  ];

  // first section
  if (hasPermission(OrganizationAdminPermissionName.IntroCalls)) {
    menuSections[0].push({
      text: 'Messages',
      path: '/chats',
      badgeCount: unreadChatsTotal,
    });
    menuSections[0].push({
      text: 'Intro calls',
      path: '/intro-calls',
      badgeCount: introRequestsDashboardNotificationTotal,
    } as $TSFixMe);
  }

  // second section
  if (hasPermission(OrganizationAdminPermissionName.ProgramViewAll)) {
    menuSections[1].push({ text: 'Programs', path: '/programs' });
  }
  if (viewer.canAccessBillableWork) {
    menuSections[1].push({ text: 'Administrative hours', path: '/admin-hours' });
  }

  // third section
  menuSections[2].push({
    text: 'Refer & earn',
    url: 'https://motivohealth.com/referrals/refer?utm_source=astraea&utm_medium=user_menu&utm_campaign=refer_and_earn',
    subtitle: 'Get $600 when you refer an organization',
  });
  menuSections[2].push({ text: 'Settings', path: '/settings' });
  if (hasPermission(OrganizationAdminPermissionName.IntroCalls)) {
    menuSections[2].push({
      text: 'Directory',
      url: 'https://app.motivohealth.com/employer-search',
    });
  }

  // fourth section
  menuSections[3].push({ text: 'Log out', path: '/logout' });

  // build final menu
  const menuLinks = menuSections
    .filter((section) => section.length > 0)
    .flatMap((section, i) => {
      if (i === 0) {
        return section;
      }
      return [{ divider: true }, ...section];
    });

  // ----------------------------------------
  // render
  // ----------------------------------------
  return (
    <MoHeader
      logoPath="/"
      isLoggedIn
      hideLinks={hideLinks}
      links={
        hideLinks
          ? []
          : headerLinks.map((link, i) => {
              if ('divider' in link) return null;
              const { path, isActive, text } = link;
              return (
                <HeaderLink key={i} component={Link} to={path} isActive={isActive}>
                  {text}
                </HeaderLink>
              );
            })
      }
      loginLink={<HeaderLink onClick={() => login()}>Log in</HeaderLink>}
      logoutLink={
        <HeaderLink component={Link} to="/logout" isLogout>
          Log out
        </HeaderLink>
      }
      menuLinks={
        hideLinks
          ? []
          : menuLinks.map((link, i) => {
              if ('divider' in link) return <Divider key={i} sx={{ my: 0.5 }} />;

              const { text, badgeCount, subtitle } = link;

              if ('onClick' in link) {
                return (
                  // @ts-expect-error TS(2722) FIXME: Cannot invoke an object which is possibly 'undefin... Remove this comment to see the full error message
                  <HeaderMenuLink key={i} onClick={() => link.onClick()} isLogout>
                    {text}
                  </HeaderMenuLink>
                );
              }

              return (
                <HeaderMenuLink
                  key={i}
                  {...(link.url
                    ? { component: 'a', href: link.url, target: '_blank', rel: 'noreferrer' }
                    : { component: Link, to: link.path })}
                >
                  <Box>
                    {text}
                    {subtitle && (
                      <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'normal' }}>
                        {subtitle}
                      </Typography>
                    )}
                  </Box>
                  {/* // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'. */}
                  {(badgeCount as $TSFixMe) > 0 && (
                    <Box ml={1}>
                      <MoNotificationBadge>{badgeCount}</MoNotificationBadge>
                    </Box>
                  )}
                </HeaderMenuLink>
              );
            })
      }
      menuButton={
        !hideLinks && (
          <>
            {mediumAvatar && (
              <Badge color="error" badgeContent={totalNotificationCount}>
                <Avatar src={mediumAvatar} />
              </Badge>
            )}
            <Box ml={2} textAlign="left">
              {fullName}
            </Box>
          </>
        )
      }
      helpButton={
        !hideLinks && (
          <Box display="flex" alignItems="center">
            <HelpOutline />
            <Box ml={0.5}>
              <Typography fontWeight={600}>Help</Typography>
            </Box>
          </Box>
        )
      }
      helpLinks={[
        <HeaderMenuLink
          key="help-center"
          component="a"
          href={HELP_ARTICLE_URLS.organizationHelpCenter}
          target="_blank"
          rel="noreferrer"
        >
          Help center
        </HeaderMenuLink>,
      ]}
      mobileLinks={
        hideLinks
          ? []
          : [...headerLinks, { divider: true }, ...menuLinks].map((link, i) => {
              if ('divider' in link) return <Divider key={i} sx={{ my: 0.5 }} />;

              const { text, badgeCount, subtitle } = link;

              if ('onClick' in link) {
                return (
                  <ListItem key={i}>
                    {/* @ts-expect-error TS(2722) FIXME: Cannot invoke an object which is possibly
            'undefin... Remove this comment to see the full error message */}
                    <BaseHeaderLink onClick={() => (link as $TSFixMe)?.onClick()} isLogout>
                      {text}
                    </BaseHeaderLink>
                  </ListItem>
                );
              }

              return (
                <ListItem key={i}>
                  <BaseHeaderLink
                    {...(link.url
                      ? { component: 'a', href: link.url, target: '_blank', rel: 'noreferrer' }
                      : { component: Link, to: link.path })}
                  >
                    <Box>
                      {text}
                      {subtitle && (
                        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'normal' }}>
                          {subtitle}
                        </Typography>
                      )}
                    </Box>
                    {/* @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'. */}
                    {badgeCount > 0 && (
                      <Box ml={1}>
                        <MoNotificationBadge> {badgeCount}</MoNotificationBadge>
                      </Box>
                    )}
                  </BaseHeaderLink>
                </ListItem>
              );
            })
      }
    />
  );
}
